import { SEOHead } from "@/components/SEOHead";
import { Layout } from "@/components/layout/Layout";
import { Link } from "react-router-dom";
import { CheckCircle, ArrowRight, AlertTriangle, Clock, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LucideIcon } from "lucide-react";

interface IndustryPageProps {
  seo: { title: string; description: string; keywords: string; canonicalUrl: string; noindex?: boolean };
  industry: string;
  badge: string;
  h1: React.ReactNode;
  subtitle: string;
  painPoints: { icon: LucideIcon; title: string; desc: string }[];
  useCases: { title: string; desc: string }[];
  stats: { stat: string; label: string }[];
  faqItems: { q: string; a: string }[];
}

export const IndustryPage = ({ seo, industry, badge, h1, subtitle, painPoints, useCases, stats, faqItems }: IndustryPageProps) => {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": `NoQ5 AI CRM Agent for ${industry}`,
      "applicationCategory": "BusinessApplication",
      "description": seo.description,
      "url": seo.canonicalUrl,
      "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
      "publisher": { "@type": "Organization", "name": "NoQ5", "url": "https://noq5.ai" }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqItems.map(f => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } }))
    }
  ];

  return (
    <Layout>
      <SEOHead {...seo} jsonLd={jsonLd} />

      <section className="pt-24 pb-16 bg-background">
        <div className="container mx-auto px-4 max-w-5xl text-center">
          <span className="inline-block px-4 py-2 bg-muted text-foreground rounded-sm text-sm font-semibold mb-6">{badge}</span>
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">{h1}</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">{subtitle}</p>
          <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground" asChild>
            <Link to="/?scrollTo=demo">Book a Demo <ArrowRight className="ml-2 h-5 w-5" /></Link>
          </Button>
        </div>
      </section>

      {stats.length > 0 && (
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((s, i) => (
                <div key={i} className="text-center">
                  <div className="text-3xl font-bold text-primary mb-1">{s.stat}</div>
                  <p className="text-muted-foreground text-sm">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-12">CRM Challenges in {industry}</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {painPoints.map((item, i) => (
              <div key={i} className="bg-card p-6 rounded-sm border border-border">
                <item.icon className="h-10 w-10 text-destructive mb-4" />
                <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-12">How NoQ5 Solves CRM for {industry}</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {useCases.map((item, i) => (
              <div key={i} className="bg-card p-6 rounded-sm border border-border">
                <CheckCircle className="h-6 w-6 text-primary mb-3" />
                <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-12">Explore More</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "CRM AI Agent", link: "/crm-agent", desc: "See the full autonomous CRM agent." },
              { title: "AI CRM Automation", link: "/ai-crm-automation", desc: "End-to-end CRM workflow automation." },
              { title: "Book a Demo", link: "/?scrollTo=demo", desc: "See NoQ5.ai on your revenue workflow." }
            ].map((item, i) => (
              <Link key={i} to={item.link} className="bg-card p-6 rounded-sm border border-border hover:border-primary/50 transition-colors group">
                <h3 className="font-bold mb-2 group-hover:text-primary">{item.title}</h3>
                <p className="text-muted-foreground text-sm">{item.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl font-bold text-center mb-12">FAQ</h2>
          <div className="space-y-6">
            {faqItems.map((item, i) => (
              <div key={i} className="bg-card p-6 rounded-sm border border-border">
                <h3 className="font-bold text-lg mb-2">{item.q}</h3>
                <p className="text-muted-foreground">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <h2 className="text-3xl font-bold mb-6">AI CRM for {industry} — Start Today</h2>
          <p className="text-xl opacity-90 mb-8">Join {industry.toLowerCase()} teams using NoQ5 to eliminate CRM admin and focus on revenue.</p>
          <Button size="lg" variant="secondary" asChild>
            <Link to="/?scrollTo=demo">Book a Demo <ArrowRight className="ml-2 h-5 w-5" /></Link>
          </Button>
        </div>
      </section>
    </Layout>
  );
};
